import { lazy, useEffect } from 'react';
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';

import { useAuth } from './context/AuthContext';
import AppShell from './components/AppShell';
import PageLoader from './components/PageLoader';

/**
 * Login is the only page imported eagerly.
 *
 * It is the cold-start screen for anyone not signed in, so splitting it would
 * buy nothing and cost a second round-trip before the first paint. Every other
 * page is behind the auth gate and cannot be the first thing rendered.
 *
 * ── Why this matters more than it looks ─────────────────────────────────────
 * These imports used to be static, which put all fourteen pages — and, through
 * components/ui/charts, the whole of recharts — into the entry graph. Vite
 * dutifully emitted a modulepreload for every chunk, so opening /login fetched
 * ~1MB of JavaScript (~294KB gzipped) to render a form with two fields. Recharts
 * alone was 110KB gzipped of that, for two screens the visitor had not reached
 * and might never have permission to see.
 *
 * Suspense lives around the Outlet in AppShell rather than around <Routes>, so
 * the sidebar and toolbar stay on screen while a page chunk arrives. A full
 * screen spinner between every navigation would read as slower than the static
 * bundle it replaced, even though less is being transferred.
 */
import Login from './pages/Login';
// Path 4's redemption page — public, for the identical reason: it can be the
// first (and only) thing a visitor with a code ever opens on this origin.
import Activate from './pages/Activate';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const TenantAnalytics = lazy(() => import('./pages/TenantAnalytics'));
const Organizations = lazy(() => import('./pages/Organizations'));
const OrganizationDetail = lazy(() => import('./pages/OrganizationDetail'));
const Users = lazy(() => import('./pages/Users'));
const Operations = lazy(() => import('./pages/Operations'));
const Technicians = lazy(() => import('./pages/Technicians'));
const TechnicianDetail = lazy(() => import('./pages/TechnicianDetail'));
const Feedback = lazy(() => import('./pages/Feedback'));
const GarageMail = lazy(() => import('./pages/GarageMail'));
const IncomingMail = lazy(() => import('./pages/IncomingMail'));
const FieldJobs = lazy(() => import('./pages/FieldJobs'));
const Audit = lazy(() => import('./pages/Audit'));
const Health = lazy(() => import('./pages/Health'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
// The distributor channel — jobcard-provisioning-flow.pdf.
const Distributors = lazy(() => import('./pages/Distributors'));
const ProRequests = lazy(() => import('./pages/ProRequests'));
const DistributorPortal = lazy(() => import('./pages/DistributorPortal'));
const DistributorGarages = lazy(() => import('./pages/DistributorGarages'));
const DistributorActivationCodes = lazy(() => import('./pages/DistributorActivationCodes'));
const DistributorProRequests = lazy(() => import('./pages/DistributorProRequests'));
const NotFound = lazy(() => import('./pages/NotFound'));

/**
 * Fetch the Dashboard chunk while the session check is still in flight.
 *
 * Restoring a session is a network round-trip, and for an authenticated visitor
 * the Dashboard is where that round-trip lands. Starting its download in
 * parallel means splitting the routes costs nothing on the one path where a
 * chunk fetch would otherwise be visible. The import is cached by the module
 * loader, so the later lazy() resolves instantly.
 *
 * Errors are swallowed deliberately: this is a hint, not a dependency. If the
 * prefetch fails the route-level import will try again and surface it properly.
 */
function usePrefetchLanding(active) {
  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;

    const warm = () => {
      if (cancelled) return;
      import('./pages/Dashboard').catch(() => {});
    };

    // requestIdleCallback where it exists (not Safari), so the prefetch never
    // competes with the work that paints the first screen.
    if (typeof requestIdleCallback === 'function') {
      const id = requestIdleCallback(warm, { timeout: 1200 });
      return () => { cancelled = true; cancelIdleCallback(id); };
    }
    const id = setTimeout(warm, 300);
    return () => { cancelled = true; clearTimeout(id); };
  }, [active]);
}

/**
 * Gate for every authenticated route.
 *
 * A UX guard only — it decides what to render, never what is allowed.
 * Authorisation is the API's job, and the API re-reads the role from the
 * database on every request. A tampered client can change what this shows and
 * still not read a byte it should not.
 */
function Protected({ children }) {
  const { isAuthenticated, bootstrapping } = useAuth();
  const location = useLocation();

  // Unconditional, before any early return — the effect inside is what gates on
  // `bootstrapping`, not the call site.
  usePrefetchLanding(bootstrapping);

  // Validating a stored token is a round-trip; showing the login screen during
  // it would flash sign-in at someone who is already signed in.
  if (bootstrapping) {
    return <PageLoader variant="full" label="Restoring session" />;
  }

  if (!isAuthenticated) {
    // Remember where they were headed so the post-login redirect lands there.
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return children;
}

/**
 * Keeps a console role out of a page it has no business on.
 *
 * Like `Protected`, this is a UX guard and not a security boundary — the API
 * enforces the same split server-side and re-reads the role from the database
 * on every request. Its job is to stop a field supervisor who types a URL from
 * staring at a permission error where a redirect would do.
 */
function RequireRole({ roles, children }) {
  const { user } = useAuth();
  if (!roles.includes(user?.role)) return <Navigate to="/" replace />;
  return children;
}

/**
 * Where "/" means, per role.
 *
 * A field supervisor has no dashboard — the platform metrics behind it are not
 * theirs to see — so their home is the queue they actually work.
 */
function Landing() {
  const { user } = useAuth();
  if (user?.role === 'field_supervisor') return <Navigate to="/field-jobs" replace />;
  if (user?.role === 'distributor') return <Navigate to="/distributor" replace />;
  return <Dashboard />;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      {/* Path 4's redemption page — public, no auth context assumed. */}
      <Route path="/activate/:code" element={<Activate />} />

      <Route path="/" element={<Protected><AppShell /></Protected>}>
        <Route index element={<Landing />} />
        {/* The one area a field supervisor can reach. */}
        <Route path="field-jobs" element={<FieldJobs />} />
        <Route path="field-jobs/:id" element={<FieldJobs />} />

        {/* The one area a distributor can reach — same shape as field-jobs
            above, reachable by both console roles it applies to. */}
        <Route path="distributor" element={<DistributorPortal />} />
        <Route path="distributor/garages" element={<DistributorGarages />} />
        <Route path="distributor/activation-codes" element={<DistributorActivationCodes />} />
        <Route path="distributor/pro-requests" element={<DistributorProRequests />} />

        {/* Everything below is superadmin-only, gated once at this layout
            route rather than page by page — the same shape as the server's
            single mount-point gate, and for the same reason: a page added here
            inherits the restriction instead of relying on someone remembering
            to add it. */}
        <Route element={<RequireRole roles={['superadmin']}><Outlet /></RequireRole>}>
          <Route path="analytics" element={<TenantAnalytics />} />
          <Route path="analytics/:id" element={<TenantAnalytics />} />

          <Route path="organizations" element={<Organizations />} />
          <Route path="organizations/:id" element={<OrganizationDetail />} />

          <Route path="users" element={<Users />} />

          {/* One screen, seven collections — the tab is a URL param so a
              filtered view of any of them is linkable. */}
          <Route path="operations" element={<Operations />} />
          <Route path="operations/:collection" element={<Operations />} />

          <Route path="technicians" element={<Technicians />} />
          <Route path="technicians/:id" element={<TechnicianDetail />} />

          <Route path="feedback" element={<Feedback />} />
          <Route path="mail" element={<GarageMail />} />
          {/* Outbound mail lives at /mail; this is the other direction. */}
          <Route path="incoming-mail" element={<IncomingMail />} />
          <Route path="audit" element={<Audit />} />
          <Route path="health" element={<Health />} />
          <Route path="landing-page" element={<LandingPage />} />
          <Route path="distributors" element={<Distributors />} />
          <Route path="distributors/:id" element={<Distributors />} />
          <Route path="pro-requests" element={<ProRequests />} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}
